import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, Loader2, Search, Filter, Edit, Trash2,
  CheckCircle, Clock, Settings, MapPin, Phone, Mail, FileText,
  X, Save, Calendar, User, Download, DollarSign, Printer
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const OrderManagement = () => {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('received');
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, provisioning: 0, active: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // New Order Form State
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [newOrderData, setNewOrderData] = useState({
    customerName: '',
    mobileNumber: '',
    alternateContact: '',
    email: '',
    officeName: '',
    companyName: '',
    designation: '',
    serviceType: '',
    fullAddress: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    latitude: '',
    longitude: '',
    planName: '',
    planSpeed: '',
    planPrice: '',
    otcAmount: '',
    installationDate: '',
    notes: '',
    priority: 'normal'
  });
  const [idProofFile, setIdProofFile] = useState(null);
  const [addressProofFile, setAddressProofFile] = useState(null);
  const [fetchingPincode, setFetchingPincode] = useState(false);
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [customerType, setCustomerType] = useState('new'); // 'new' or 'existing'

  // Service and Plan options (matching customer order form)
  const services = [
    {
      name: 'Internet Leased Line',
      plans: [
        { speed: '5 Mbps', name: 'ILL 5M' },
        { speed: '10 Mbps', name: 'ILL 10M' },
        { speed: '50 Mbps', name: 'ILL 50M' },
        { speed: '100 Mbps', name: 'ILL 100M' },
        { speed: '500 Mbps', name: 'ILL 500M' },
        { speed: '1 Gbps', name: 'ILL 1G' }
      ]
    },
    {
      name: 'Broadband Services',
      plans: [
        { speed: '5 Mbps', name: 'Broadband Starter' },
        { speed: '50 Mbps', name: 'Broadband Basic' },
        { speed: '100 Mbps', name: 'Broadband Standard' },
        { speed: '200 Mbps', name: 'Broadband Premium' },
        { speed: '500 Mbps', name: 'Broadband Ultra' },
        { speed: '1 Gbps', name: 'Broadband Elite' }
      ]
    },
    {
      name: 'OTT Services',
      plans: [
        { speed: '5 Mbps', name: 'OTT Starter' },
        { speed: '50 Mbps', name: 'OTT Basic' },
        { speed: '100 Mbps', name: 'OTT Standard' },
        { speed: '200 Mbps', name: 'OTT Premium' }
      ]
    },
    {
      name: 'VoIP Services',
      plans: [
        { speed: 'Basic', name: 'VoIP Basic Plan' },
        { speed: 'Business', name: 'VoIP Business Plan' },
        { speed: 'Enterprise', name: 'VoIP Enterprise Plan' }
      ]
    },
    {
      name: 'FTTH',
      plans: [
        { speed: '5 Mbps', name: 'FTTH 5M' },
        { speed: '50 Mbps', name: 'FTTH 50M' },
        { speed: '100 Mbps', name: 'FTTH 100M' },
        { speed: '200 Mbps', name: 'FTTH 200M' },
        { speed: '500 Mbps', name: 'FTTH 500M' },
        { speed: '1 Gbps', name: 'FTTH 1G' }
      ]
    }
  ];

  // Get available plans based on selected service
  const getAvailablePlans = () => {
    const selectedService = services.find(s => s.name === newOrderData.serviceType);
    return selectedService ? selectedService.plans : [];
  };

  useEffect(() => {
    const token = localStorage.getItem('customerAdminToken');
    if (!token) {
      navigate('/customer-admin/login');
      return;
    }
    
    fetchOrders();
    fetchStats();
  }, [navigate, statusFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerAdminToken');
      const url = statusFilter 
        ? `${API_BASE_URL}/api/customer-admin/orders?status=${statusFilter}`
        : `${API_BASE_URL}/api/customer-admin/orders`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      } else {
        toast.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error loading orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/orders/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchExistingCustomers = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Extract unique customers from orders
        const customersMap = new Map();
        (data.orders || []).forEach(order => {
          const customerName = order.customerName || order.fullName;
          if (customerName && order.mobileNumber) {
            const key = `${customerName}-${order.mobileNumber}`;
            if (!customersMap.has(key)) {
              customersMap.set(key, {
                customerName: customerName,
                mobileNumber: order.mobileNumber,
                alternateContact: order.alternateContact || '',
                email: order.email || '',
                officeName: order.officeName || '',
                companyName: order.companyName || '',
                designation: order.designation || '',
                fullAddress: order.address || order.fullAddress || '',
                city: order.city || '',
                state: order.state || '',
                country: order.country || 'India',
                pincode: order.pinCode || order.pincode || ''
              });
            }
          }
        });
        setExistingCustomers(Array.from(customersMap.values()));
      }
    } catch (error) {
      console.error('Error fetching existing customers:', error);
    }
  };

  const handleSelectExistingCustomer = (customerJson) => {
    if (!customerJson) return;
    
    try {
      const customer = JSON.parse(customerJson);
      setNewOrderData({
        ...newOrderData,
        customerName: customer.customerName || '',
        mobileNumber: customer.mobileNumber || '',
        alternateContact: customer.alternateContact || '',
        email: customer.email || '',
        officeName: customer.officeName || '',
        companyName: customer.companyName || '',
        designation: customer.designation || '',
        fullAddress: customer.fullAddress || '',
        city: customer.city || '',
        state: customer.state || '',
        country: customer.country || 'India',
        pincode: customer.pincode || ''
      });
      toast.success(`Customer details loaded: ${customer.customerName}`);
    } catch (error) {
      console.error('Error parsing customer data:', error);
      toast.error('Failed to load customer details');
    }
  };


  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Order deleted successfully');
        fetchOrders();
        fetchStats();
      } else {
        toast.error('Failed to delete order');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Error deleting order');
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order.orderId);
    setEditFormData({
      fullName: order.fullName,
      mobileNumber: order.mobileNumber,
      email: order.email,
      address: order.address,
      officeName: order.officeName || '',
      city: order.city,
      state: order.state,
      pinCode: order.pinCode,
      selectedService: order.selectedService,
      selectedPlan: order.selectedPlan,
      status: order.status,
      provisioningStatus: order.provisioningStatus || '',
      activationDate: order.activationDate || '',
      additionalNotes: order.adminNotes || '',
      // Device Details
      routerModel: order.deviceDetails?.routerModel || '',
      installationDate: order.deviceDetails?.installationDate || '',
      deviceActivationDate: order.deviceDetails?.deviceActivationDate || '',
      routerUsername: order.deviceDetails?.routerUsername || '',
      routerPassword: order.deviceDetails?.routerPassword || '',
      ipAddress: order.deviceDetails?.ipAddress || '',
      wanIp: order.deviceDetails?.wanIp || '',
      lanIp: order.deviceDetails?.lanIp || '',
      // Additional Device Details
      circuitId: order.deviceDetails?.circuitId || '',
      ispName: order.deviceDetails?.ispName || '',
      pricePerMbps: order.deviceDetails?.pricePerMbps || '',
      pricePerDay: order.deviceDetails?.pricePerDay || '',
      billingTerm: order.deviceDetails?.billingTerm || '',
      otcAmount: order.deviceDetails?.otcAmount || ''
    });
  };

  const handleSaveOrder = async (orderId) => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const formData = new FormData();
      
      Object.keys(editFormData).forEach(key => {
        if (editFormData[key]) {
          formData.append(key, editFormData[key]);
        }
      });
      
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Order updated successfully');
        setEditingOrder(null);
        fetchOrders();
        fetchStats();
      } else {
        toast.error('Failed to update order');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Error updating order');
    }
  };

  const handleDownloadOrder = async (orderId) => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/orders/${orderId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download error:', errorText);
        throw new Error(`Failed to download order: ${response.status}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Order_${orderId}.docx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Order downloaded successfully');
    } catch (error) {
      console.error('Error downloading order:', error);
      toast.error(`Failed to download order: ${error.message}`);
    }
  };

  const handlePrintOrder = (order) => {
    // Create a printable HTML content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order ${order.orderId}</title>
        <style>
          @media print {
            @page { margin: 1cm; }
            body { margin: 0; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            border-bottom: 3px solid #10b981;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .header h1 {
            color: #10b981;
            margin: 0;
          }
          .order-id {
            font-size: 24px;
            color: #059669;
            font-weight: bold;
            margin: 10px 0;
          }
          .section {
            margin-bottom: 25px;
            border: 1px solid #e5e7eb;
            padding: 15px;
            border-radius: 8px;
          }
          .section-title {
            font-size: 18px;
            font-weight: bold;
            color: #374151;
            margin-bottom: 15px;
            border-bottom: 2px solid #10b981;
            padding-bottom: 8px;
          }
          .row {
            display: flex;
            margin-bottom: 10px;
          }
          .label {
            font-weight: bold;
            width: 180px;
            color: #6b7280;
          }
          .value {
            flex: 1;
            color: #111827;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
          }
          .status-pending { background: #fef3c7; color: #92400e; }
          .status-provisioning { background: #dbeafe; color: #1e40af; }
          .status-active { background: #d1fae5; color: #065f46; }
          .status-cancelled { background: #fee2e2; color: #991b1b; }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
            text-align: center;
            color: #6b7280;
            font-size: 12px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🌐 Netix Fibernet</h1>
          <p>Service Order Details</p>
          <div class="order-id">Order ID: ${order.orderId}</div>
          <div>
            <span class="status-badge status-${order.status?.toLowerCase() || 'pending'}">
              ${order.status?.toUpperCase() || 'PENDING'}
            </span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">📋 Customer Information</div>
          <div class="row">
            <div class="label">Customer Name:</div>
            <div class="value">${order.customerName || order.fullName || 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">Mobile Number:</div>
            <div class="value">${order.mobileNumber || 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">Email:</div>
            <div class="value">${order.email || 'N/A'}</div>
          </div>
          ${order.alternateContact ? `
          <div class="row">
            <div class="label">Alternate Contact:</div>
            <div class="value">${order.alternateContact}</div>
          </div>
          ` : ''}
          ${order.companyName ? `
          <div class="row">
            <div class="label">Company Name:</div>
            <div class="value">${order.companyName}</div>
          </div>
          ` : ''}
          ${order.designation ? `
          <div class="row">
            <div class="label">Designation:</div>
            <div class="value">${order.designation}</div>
          </div>
          ` : ''}
        </div>

        <div class="section">
          <div class="section-title">📍 Address Information</div>
          <div class="row">
            <div class="label">Address:</div>
            <div class="value">${order.fullAddress || order.address || 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">City:</div>
            <div class="value">${order.city || 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">State:</div>
            <div class="value">${order.state || 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">Pincode:</div>
            <div class="value">${order.pincode || order.pinCode || 'N/A'}</div>
          </div>
          ${order.officeName ? `
          <div class="row">
            <div class="label">Office Name:</div>
            <div class="value">${order.officeName}</div>
          </div>
          ` : ''}
        </div>

        <div class="section">
          <div class="section-title">🌐 Service Details</div>
          <div class="row">
            <div class="label">Service Type:</div>
            <div class="value">${order.serviceType || order.selectedService || 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">Plan:</div>
            <div class="value">${order.planName || order.selectedPlan || 'N/A'}</div>
          </div>
          ${order.planPrice ? `
          <div class="row">
            <div class="label">Plan Price:</div>
            <div class="value">₹${order.planPrice}</div>
          </div>
          ` : ''}
          ${order.otcAmount ? `
          <div class="row">
            <div class="label">OTC Amount:</div>
            <div class="value">₹${order.otcAmount}</div>
          </div>
          ` : ''}
          ${order.totalAmount ? `
          <div class="row">
            <div class="label">Total Amount:</div>
            <div class="value"><strong>₹${order.totalAmount}</strong></div>
          </div>
          ` : ''}
          ${order.installationDate ? `
          <div class="row">
            <div class="label">Installation Date:</div>
            <div class="value">${order.installationDate}</div>
          </div>
          ` : ''}
        </div>

        ${order.notes || order.additionalNotes ? `
        <div class="section">
          <div class="section-title">📝 Additional Notes</div>
          <div class="value">${order.notes || order.additionalNotes}</div>
        </div>
        ` : ''}

        <div class="section">
          <div class="section-title">📅 Order Timeline</div>
          <div class="row">
            <div class="label">Submitted At:</div>
            <div class="value">${order.submittedAt ? new Date(order.submittedAt).toLocaleString() : 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">Last Updated:</div>
            <div class="value">${order.updatedAt ? new Date(order.updatedAt).toLocaleString() : 'N/A'}</div>
          </div>
        </div>

        <div class="footer">
          <p><strong>Netix Fibernet</strong> - High-Speed Internet Solutions</p>
          <p>Printed on: ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load, then print
      printWindow.onload = function() {
        printWindow.focus();
        printWindow.print();
      };
      
      toast.success('Opening print preview...');
    } else {
      toast.error('Please allow pop-ups to print orders');
    }
  };

  const handlePincodeLookup = async (pincode) => {
    if (pincode.length !== 6 || !/^\d+$/.test(pincode)) {
      return;
    }

    try {
      setFetchingPincode(true);
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/pincode-lookup/${pincode}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success && data.data) {
        setNewOrderData(prev => ({
          ...prev,
          city: data.data.city,
          state: data.data.state,
          country: data.data.country
        }));
        toast.success('Pincode details fetched successfully!');
      } else {
        toast.error('Unable to fetch pincode details');
      }
    } catch (error) {
      console.error('Error fetching pincode:', error);
      toast.error('Failed to fetch pincode details');
    } finally {
      setFetchingPincode(false);
    }
  };

  const handleCreateNewOrder = async () => {
    // Validation
    if (!newOrderData.customerName || !newOrderData.mobileNumber || !newOrderData.email || 
        !newOrderData.serviceType || !newOrderData.fullAddress || !newOrderData.city || 
        !newOrderData.state || !newOrderData.pincode || !newOrderData.planName || 
        !newOrderData.planPrice || !newOrderData.otcAmount) {
      toast.error('Please fill all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newOrderData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Mobile validation
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(newOrderData.mobileNumber)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      setCreatingOrder(true);
      const token = localStorage.getItem('customerAdminToken');
      
      const formData = new FormData();
      Object.keys(newOrderData).forEach(key => {
        formData.append(key, newOrderData[key]);
      });
      
      if (idProofFile) {
        formData.append('idProof', idProofFile);
      }
      
      if (addressProofFile) {
        formData.append('addressProof', addressProofFile);
      }

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/create-manual-order`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Order ${data.orderId} created successfully!`);
        setShowNewOrderModal(false);
        // Reset form
        setNewOrderData({
          customerName: '',
          mobileNumber: '',
          alternateContact: '',
          email: '',
          officeName: '',
          companyName: '',
          designation: '',
          serviceType: '',
          fullAddress: '',
          city: '',
          state: '',
          country: 'India',
          pincode: '',
          latitude: '',
          longitude: '',
          planName: '',
          planSpeed: '',
          planPrice: '',
          otcAmount: '',
          installationDate: '',
          notes: '',
          priority: 'normal'
        });
        setIdProofFile(null);
        setAddressProofFile(null);
        // Refresh orders list
        fetchOrders();
        fetchStats();
      } else {
        toast.error(data.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const customerName = order.fullName || order.customerName || '';
    const matchesSearch = 
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.mobileNumber?.includes(searchTerm) ||
      order.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === 'received') {
      return matchesSearch && order.status === 'pending';
    } else if (activeTab === 'provisioning') {
      return matchesSearch && order.status === 'provisioning';
    } else if (activeTab === 'activation') {
      return matchesSearch && (order.status === 'active' || order.status === 'completed');
    }
    
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Pending' },
      provisioning: { color: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Provisioning' },
      active: { color: 'bg-green-100 text-green-800 border-green-300', label: 'Active' },
      completed: { color: 'bg-green-100 text-green-800 border-green-300', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800 border-red-300', label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border-2 ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/customer-admin/dashboard')}
              variant="outline"
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Order Management</h1>
              <p className="text-gray-600">Manage and track service orders</p>
            </div>
          </div>
          <Button
            onClick={() => {
              setShowNewOrderModal(true);
              fetchExistingCustomers();
            }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 gap-2"
          >
            <Package className="h-4 w-4" />
            Place New Order
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <Card className="border-2 border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <Package className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-yellow-200 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700">Pending</p>
                  <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">Provisioning</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.provisioning}</p>
                </div>
                <Settings className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">Active</p>
                  <p className="text-2xl font-bold text-green-900">{stats.active}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700">Cancelled</p>
                  <p className="text-2xl font-bold text-red-900">{stats.cancelled}</p>
                </div>
                <X className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('received')}
            variant={activeTab === 'received' ? 'default' : 'outline'}
            className={activeTab === 'received' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
          >
            <Package className="h-4 w-4 mr-2" />
            Orders Received ({stats.pending})
          </Button>
          <Button
            onClick={() => setActiveTab('provisioning')}
            variant={activeTab === 'provisioning' ? 'default' : 'outline'}
            className={activeTab === 'provisioning' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            <Settings className="h-4 w-4 mr-2" />
            Provisioning Services ({stats.provisioning})
          </Button>
          <Button
            onClick={() => setActiveTab('activation')}
            variant={activeTab === 'activation' ? 'default' : 'outline'}
            className={activeTab === 'activation' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Configure & Activation ({stats.active})
          </Button>
        </div>

        {/* Search and Filter */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, order ID, mobile, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="provisioning">Provisioning</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No orders found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <Card key={order.orderId} className="border-2 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  {editingOrder === order.orderId ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900">Edit Order: {order.orderId}</h3>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleSaveOrder(order.orderId)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Save className="h-4 w-4 mr-2" />
                            Save
                          </Button>
                          <Button
                            onClick={() => setEditingOrder(null)}
                            size="sm"
                            variant="outline"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Full Name</Label>
                          <Input
                            value={editFormData.fullName}
                            onChange={(e) => setEditFormData({...editFormData, fullName: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Mobile Number</Label>
                          <Input
                            value={editFormData.mobileNumber}
                            onChange={(e) => setEditFormData({...editFormData, mobileNumber: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            value={editFormData.email}
                            onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Office Name</Label>
                          <Input
                            value={editFormData.officeName}
                            onChange={(e) => setEditFormData({...editFormData, officeName: e.target.value})}
                            placeholder="Branch/Office name"
                          />
                        </div>
                        <div>
                          <Label>Status</Label>
                          <select
                            value={editFormData.status}
                            onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                            className="w-full px-3 py-2 border rounded-md"
                          >
                            <option value="pending">Pending</option>
                            <option value="provisioning">Provisioning</option>
                            <option value="active">Active</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                        <div>
                          <Label>Provisioning Status</Label>
                          <Input
                            value={editFormData.provisioningStatus}
                            onChange={(e) => setEditFormData({...editFormData, provisioningStatus: e.target.value})}
                            placeholder="e.g., Equipment ordered, Installation scheduled"
                          />
                        </div>
                        <div>
                          <Label>Activation Date</Label>
                          <Input
                            type="date"
                            value={editFormData.activationDate}
                            onChange={(e) => setEditFormData({...editFormData, activationDate: e.target.value})}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label>Admin Notes</Label>
                          <Input
                            value={editFormData.additionalNotes}
                            onChange={(e) => setEditFormData({...editFormData, additionalNotes: e.target.value})}
                            placeholder="Internal notes..."
                          />
                        </div>
                      </div>

                      {/* Device Details Section */}
                      <div className="mt-6 pt-6 border-t-2 border-blue-200">
                        <div className="flex items-center gap-2 mb-4">
                          <Settings className="h-5 w-5 text-blue-600" />
                          <h4 className="text-lg font-bold text-gray-900">Device Details</h4>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4 bg-blue-50 p-4 rounded-lg">
                          <div>
                            <Label>Router Model/Type</Label>
                            <Input
                              value={editFormData.routerModel}
                              onChange={(e) => setEditFormData({...editFormData, routerModel: e.target.value})}
                              placeholder="e.g., TP-Link Archer C6, D-Link DIR-825"
                            />
                          </div>
                          <div>
                            <Label>Installation Date</Label>
                            <Input
                              type="date"
                              value={editFormData.installationDate}
                              onChange={(e) => setEditFormData({...editFormData, installationDate: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Device Activation Date</Label>
                            <Input
                              type="date"
                              value={editFormData.deviceActivationDate}
                              onChange={(e) => setEditFormData({...editFormData, deviceActivationDate: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Router Username</Label>
                            <Input
                              value={editFormData.routerUsername}
                              onChange={(e) => setEditFormData({...editFormData, routerUsername: e.target.value})}
                              placeholder="Router admin username"
                            />
                          </div>
                          <div>
                            <Label>Router Password</Label>
                            <Input
                              type="text"
                              value={editFormData.routerPassword}
                              onChange={(e) => setEditFormData({...editFormData, routerPassword: e.target.value})}
                              placeholder="Router admin password"
                            />
                          </div>
                          <div>
                            <Label>IP Address</Label>
                            <Input
                              value={editFormData.ipAddress}
                              onChange={(e) => setEditFormData({...editFormData, ipAddress: e.target.value})}
                              placeholder="e.g., 192.168.1.1"
                            />
                          </div>
                          <div>
                            <Label>WAN IP Address</Label>
                            <Input
                              value={editFormData.wanIp}
                              onChange={(e) => setEditFormData({...editFormData, wanIp: e.target.value})}
                              placeholder="e.g., 103.25.45.67"
                            />
                          </div>
                          <div>
                            <Label>LAN IP Address</Label>
                            <Input
                              value={editFormData.lanIp}
                              onChange={(e) => setEditFormData({...editFormData, lanIp: e.target.value})}
                              placeholder="e.g., 192.168.1.1"
                            />
                          </div>
                        </div>

                        {/* ISP & Circuit Details */}
                        <div className="mt-6 pt-6 border-t-2 border-purple-200">
                          <h5 className="text-md font-bold text-gray-900 mb-3">ISP & Circuit Details</h5>
                          <div className="grid md:grid-cols-2 gap-4 bg-purple-50 p-4 rounded-lg">
                            <div>
                              <Label>Circuit ID</Label>
                              <Input
                                value={editFormData.circuitId}
                                onChange={(e) => setEditFormData({...editFormData, circuitId: e.target.value})}
                                placeholder="Enter circuit ID"
                              />
                            </div>
                            <div>
                              <Label>ISP Name</Label>
                              <select
                                value={editFormData.ispName}
                                onChange={(e) => setEditFormData({...editFormData, ispName: e.target.value})}
                                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              >
                                <option value="">Select ISP</option>
                                <option value="Bharti Airtel">Bharti Airtel</option>
                                <option value="Jio">Jio</option>
                                <option value="Tata">Tata</option>
                                <option value="Netra">Netra</option>
                                <option value="ANI Network">ANI Network</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Pricing Details */}
                        <div className="mt-6 pt-6 border-t-2 border-green-200">
                          <h5 className="text-md font-bold text-gray-900 mb-3">Pricing Details</h5>
                          <div className="grid md:grid-cols-2 gap-4 bg-green-50 p-4 rounded-lg">
                            <div>
                              <Label>Price per MBPS (₹)</Label>
                              <Input
                                type="number"
                                value={editFormData.pricePerMbps}
                                onChange={(e) => setEditFormData({...editFormData, pricePerMbps: e.target.value})}
                                placeholder="Enter price per MBPS"
                              />
                            </div>
                            <div>
                              <Label>Price per Day (₹)</Label>
                              <Input
                                type="number"
                                value={editFormData.pricePerDay}
                                onChange={(e) => setEditFormData({...editFormData, pricePerDay: e.target.value})}
                                placeholder="Enter price per day"
                              />
                            </div>
                            <div>
                              <Label>Customer Billing Term</Label>
                              <select
                                value={editFormData.billingTerm}
                                onChange={(e) => setEditFormData({...editFormData, billingTerm: e.target.value})}
                                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                              >
                                <option value="">Select Billing Term</option>
                                <option value="Monthly">Monthly</option>
                                <option value="Quarterly">Quarterly</option>
                                <option value="Half Annually">Half Annually</option>
                                <option value="Annually">Annually</option>
                              </select>
                            </div>
                            <div>
                              <Label>OTC Amount (₹)</Label>
                              <Input
                                type="number"
                                value={editFormData.otcAmount}
                                onChange={(e) => setEditFormData({...editFormData, otcAmount: e.target.value})}
                                placeholder="One Time Charge"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-900">{order.fullName || order.customerName}</h3>
                            {getStatusBadge(order.status)}
                          </div>
                          <p className="text-sm text-gray-600">Order ID: <span className="font-semibold">{order.orderId}</span></p>
                          <p className="text-sm text-gray-600">Submitted: {formatDate(order.submittedAt)}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleDownloadOrder(order.orderId)}
                            size="sm"
                            className="gap-2 bg-green-600 hover:bg-green-700"
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </Button>
                          <Button
                            onClick={() => handlePrintOrder(order)}
                            size="sm"
                            className="gap-2 bg-blue-600 hover:bg-blue-700"
                          >
                            <Printer className="h-4 w-4" />
                            Print
                          </Button>
                          <Button
                            onClick={() => handleEditOrder(order)}
                            size="sm"
                            variant="outline"
                            className="gap-2"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleDeleteOrder(order.orderId)}
                            size="sm"
                            variant="destructive"
                            className="gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </div>

                      {/* Customer Details Section */}
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
                        <h5 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
                          <User className="h-4 w-4" /> Customer Details
                        </h5>
                        <div className="grid md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-gray-600">Name</p>
                            <p className="font-semibold text-gray-900">{order.customerName || order.fullName}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Mobile</p>
                            <p className="font-semibold flex items-center gap-1">
                              <Phone className="h-3 w-3 text-blue-600" /> {order.mobileNumber}
                            </p>
                          </div>
                          {order.alternateContact && (
                            <div>
                              <p className="text-gray-600">Alternate Contact</p>
                              <p className="font-semibold flex items-center gap-1">
                                <Phone className="h-3 w-3 text-green-600" /> {order.alternateContact}
                              </p>
                            </div>
                          )}
                          <div>
                            <p className="text-gray-600">Email</p>
                            <p className="font-semibold flex items-center gap-1">
                              <Mail className="h-3 w-3 text-red-600" /> {order.email}
                            </p>
                          </div>
                          {order.companyName && (
                            <div>
                              <p className="text-gray-600">Company Name</p>
                              <p className="font-semibold text-purple-700">{order.companyName}</p>
                            </div>
                          )}
                          {order.officeName && (
                            <div>
                              <p className="text-gray-600">Office Name</p>
                              <p className="font-semibold text-gray-800">{order.officeName}</p>
                            </div>
                          )}
                          {order.designation && (
                            <div className="md:col-span-2">
                              <p className="text-gray-600">Designation</p>
                              <p className="font-semibold text-indigo-700">{order.designation}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 mb-1 flex items-center gap-2">
                            <Package className="h-4 w-4" /> Service Details
                          </p>
                          <p className="font-semibold text-blue-700">{order.serviceType || order.selectedService}</p>
                          <p className="text-gray-700">Plan: {order.planName || order.selectedPlan}</p>
                          {order.planSpeed && <p className="text-gray-600 text-xs">Speed: {order.planSpeed}</p>}
                        </div>
                        
                        <div>
                          <p className="text-gray-600 mb-1 flex items-center gap-2">
                            <MapPin className="h-4 w-4" /> Location
                          </p>
                          <p className="font-semibold">{order.city}, {order.state}</p>
                          <p className="text-gray-700">PIN: {order.pincode || order.pinCode}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1 flex items-center gap-2">
                            <DollarSign className="h-4 w-4" /> Pricing
                          </p>
                          <p className="font-semibold text-green-700">₹{order.planPrice}/month</p>
                          <p className="text-gray-600 text-xs">OTC: ₹{order.otcAmount}</p>
                          <p className="text-gray-700 font-bold">Total: ₹{order.totalAmount}</p>
                        </div>
                      </div>

                      {order.provisioningStatus && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm font-semibold text-blue-900 mb-1">Provisioning Status:</p>
                          <p className="text-sm text-blue-700">{order.provisioningStatus}</p>
                        </div>
                      )}

                      {order.adminNotes && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                            <FileText className="h-4 w-4" /> Admin Notes:
                          </p>
                          <p className="text-sm text-gray-700">{order.adminNotes}</p>
                        </div>
                      )}

                      {order.activationDate && (
                        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm font-semibold text-green-900 mb-1 flex items-center gap-2">
                            <Calendar className="h-4 w-4" /> Activation Date:
                          </p>
                          <p className="text-sm text-green-700">{formatDate(order.activationDate)}</p>
                        </div>
                      )}

                      {/* Device Details Display */}
                      {order.deviceDetails && Object.keys(order.deviceDetails).length > 0 && (
                        <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-300">
                          <p className="text-base font-bold text-blue-900 mb-3 flex items-center gap-2">
                            <Settings className="h-5 w-5" /> Device Details
                          </p>
                          <div className="grid md:grid-cols-2 gap-3 text-sm">
                            {order.deviceDetails.routerModel && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">Router Model:</p>
                                <p className="text-gray-900">{order.deviceDetails.routerModel}</p>
                              </div>
                            )}
                            {order.deviceDetails.installationDate && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">Installation Date:</p>
                                <p className="text-gray-900">{formatDate(order.deviceDetails.installationDate)}</p>
                              </div>
                            )}
                            {order.deviceDetails.deviceActivationDate && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">Device Activation:</p>
                                <p className="text-gray-900">{formatDate(order.deviceDetails.deviceActivationDate)}</p>
                              </div>
                            )}
                            {order.deviceDetails.routerUsername && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">Router Username:</p>
                                <p className="text-gray-900 font-mono">{order.deviceDetails.routerUsername}</p>
                              </div>
                            )}
                            {order.deviceDetails.routerPassword && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">Router Password:</p>
                                <p className="text-gray-900 font-mono">{order.deviceDetails.routerPassword}</p>
                              </div>
                            )}
                            {order.deviceDetails.ipAddress && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">IP Address:</p>
                                <p className="text-gray-900 font-mono">{order.deviceDetails.ipAddress}</p>
                              </div>
                            )}
                            {order.deviceDetails.wanIp && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">WAN IP:</p>
                                <p className="text-gray-900 font-mono">{order.deviceDetails.wanIp}</p>
                              </div>
                            )}
                            {order.deviceDetails.lanIp && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">LAN IP:</p>
                                <p className="text-gray-900 font-mono">{order.deviceDetails.lanIp}</p>
                              </div>
                            )}
                            {order.deviceDetails.circuitId && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">Circuit ID:</p>
                                <p className="text-gray-900 font-mono">{order.deviceDetails.circuitId}</p>
                              </div>
                            )}
                            {order.deviceDetails.ispName && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">ISP Name:</p>
                                <p className="text-gray-900">{order.deviceDetails.ispName}</p>
                              </div>
                            )}
                            {order.deviceDetails.pricePerMbps && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">Price per MBPS:</p>
                                <p className="text-gray-900">₹{order.deviceDetails.pricePerMbps}</p>
                              </div>
                            )}
                            {order.deviceDetails.pricePerDay && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">Price per Day:</p>
                                <p className="text-gray-900">₹{order.deviceDetails.pricePerDay}</p>
                              </div>
                            )}
                            {order.deviceDetails.billingTerm && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">Billing Term:</p>
                                <p className="text-gray-900">{order.deviceDetails.billingTerm}</p>
                              </div>
                            )}
                            {order.deviceDetails.otcAmount && (
                              <div className="bg-white p-2 rounded">
                                <p className="text-gray-600 font-semibold">OTC Amount:</p>
                                <p className="text-gray-900">₹{order.deviceDetails.otcAmount}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* New Order Modal */}
        {showNewOrderModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto">
            <Card className="w-full max-w-5xl my-8 mx-4 max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header - Fixed */}
              <div className="px-6 py-4 border-b bg-white sticky top-0 z-10">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Place New Order</h3>
                    <p className="text-sm text-gray-500">Fill in details to create order for existing or new customer</p>
                  </div>
                  <button
                    onClick={() => setShowNewOrderModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content - Scrollable */}
              <CardContent className="flex-1 overflow-y-auto px-6 py-4">
                <div className="space-y-4">
                  {/* Customer Information Section */}
                  <div className="bg-blue-50 p-2 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Customer Details
                    </h4>
                    
                    {/* Select Customer Type */}
                    <div className="mb-3 p-3 bg-white rounded border border-blue-300">
                      <Label className="text-blue-900 font-semibold text-base">Select Customer *</Label>
                      <div className="space-y-2 mt-2">
                        {/* Customer Type Selection */}
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="customerType"
                              value="new"
                              checked={customerType === 'new'}
                              onChange={(e) => {
                                setCustomerType(e.target.value);
                                // Clear form when switching to new customer
                                setNewOrderData({
                                  ...newOrderData,
                                  customerName: '',
                                  mobileNumber: '',
                                  alternateContact: '',
                                  email: '',
                                  officeName: '',
                                  companyName: '',
                                  designation: '',
                                  fullAddress: '',
                                  city: '',
                                  state: '',
                                  country: 'India',
                                  pincode: ''
                                });
                              }}
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="font-semibold text-gray-700">New Customer</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="customerType"
                              value="existing"
                              checked={customerType === 'existing'}
                              onChange={(e) => setCustomerType(e.target.value)}
                              className="w-4 h-4 text-blue-600"
                            />
                            <span className="font-semibold text-gray-700">Existing Customer</span>
                          </label>
                        </div>

                        {/* Existing Customer Dropdown - Only show when Existing Customer is selected */}
                        {customerType === 'existing' && (
                          <div className="mt-2">
                            <select
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onChange={(e) => handleSelectExistingCustomer(e.target.value)}
                              defaultValue=""
                            >
                              <option value="">-- Select Customer --</option>
                              {existingCustomers.map((customer, idx) => (
                                <option key={idx} value={JSON.stringify(customer)}>
                                  {customer.customerName} - {customer.mobileNumber}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-600 mt-1">Select a customer to auto-populate their details</p>
                          </div>
                        )}

                        {customerType === 'new' && (
                          <p className="text-xs text-green-600 mt-1">✓ Fill in details below for new customer</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-sm font-semibold">Customer Name *</Label>
                        <Input
                          value={newOrderData.customerName}
                          onChange={(e) => setNewOrderData({...newOrderData, customerName: e.target.value})}
                          placeholder="Full name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold">Mobile Number *</Label>
                        <Input
                          type="tel"
                          value={newOrderData.mobileNumber}
                          onChange={(e) => setNewOrderData({...newOrderData, mobileNumber: e.target.value})}
                          placeholder="10-digit mobile"
                          maxLength="10"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold">Alternate Contact</Label>
                        <Input
                          type="tel"
                          value={newOrderData.alternateContact}
                          onChange={(e) => setNewOrderData({...newOrderData, alternateContact: e.target.value})}
                          placeholder="Alternate number"
                          maxLength="10"
                          className="mt-1"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-sm font-semibold">Email Address *</Label>
                        <Input
                          type="email"
                          value={newOrderData.email}
                          onChange={(e) => setNewOrderData({...newOrderData, email: e.target.value})}
                          placeholder="customer@example.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold">Designation</Label>
                        <Input
                          value={newOrderData.designation}
                          onChange={(e) => setNewOrderData({...newOrderData, designation: e.target.value})}
                          placeholder="Job title"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold">Company Name</Label>
                        <Input
                          value={newOrderData.companyName}
                          onChange={(e) => setNewOrderData({...newOrderData, companyName: e.target.value})}
                          placeholder="Company/Organization"
                          className="mt-1"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-sm font-semibold">Office Name</Label>
                        <Input
                          value={newOrderData.officeName}
                          onChange={(e) => setNewOrderData({...newOrderData, officeName: e.target.value})}
                          placeholder="Branch/Office name"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address & Location Section */}
                  <div className="bg-green-50 p-2 rounded-lg border border-green-200">
                    <h4 className="text-sm font-semibold text-green-900 mb-2 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Address & Location
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <Label>Full Address *</Label>
                        <textarea
                          className="w-full min-h-[60px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={newOrderData.fullAddress}
                          onChange={(e) => setNewOrderData({...newOrderData, fullAddress: e.target.value})}
                          placeholder="House/Flat No., Building, Street, Area, Landmark"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <Label>Pincode *</Label>
                          <div className="flex gap-2">
                            <Input
                              value={newOrderData.pincode}
                              onChange={(e) => {
                                const value = e.target.value;
                                setNewOrderData({...newOrderData, pincode: value});
                                if (value.length === 6) {
                                  handlePincodeLookup(value);
                                }
                              }}
                              placeholder="6-digit pincode"
                              maxLength="6"
                              className="flex-1"
                            />
                            {fetchingPincode && <Loader2 className="h-8 w-8 animate-spin text-blue-600" />}
                          </div>
                        </div>
                        <div>
                          <Label>City *</Label>
                          <Input
                            value={newOrderData.city}
                            onChange={(e) => setNewOrderData({...newOrderData, city: e.target.value})}
                            placeholder="Auto-filled from pincode"
                            readOnly={fetchingPincode}
                          />
                        </div>
                        <div>
                          <Label>State *</Label>
                          <Input
                            value={newOrderData.state}
                            onChange={(e) => setNewOrderData({...newOrderData, state: e.target.value})}
                            placeholder="Auto-filled from pincode"
                            readOnly={fetchingPincode}
                          />
                        </div>
                        <div>
                          <Label>Country *</Label>
                          <Input
                            value={newOrderData.country}
                            onChange={(e) => setNewOrderData({...newOrderData, country: e.target.value})}
                            placeholder="India"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <Label>Latitude (Optional)</Label>
                          <Input
                            value={newOrderData.latitude}
                            onChange={(e) => setNewOrderData({...newOrderData, latitude: e.target.value})}
                            placeholder="e.g., 28.6139"
                          />
                        </div>
                        <div>
                          <Label>Longitude (Optional)</Label>
                          <Input
                            value={newOrderData.longitude}
                            onChange={(e) => setNewOrderData({...newOrderData, longitude: e.target.value})}
                            placeholder="e.g., 77.2090"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Service & Pricing Section */}
                  <div className="bg-purple-50 p-2 rounded-lg border border-purple-200">
                    <h4 className="text-sm font-semibold text-purple-900 mb-2 flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Service & Pricing Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <Label className="font-semibold">Select Service *</Label>
                        <select
                          className="w-full px-3 py-2 border-2 border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white font-medium"
                          value={newOrderData.serviceType}
                          onChange={(e) => {
                            setNewOrderData({
                              ...newOrderData, 
                              serviceType: e.target.value,
                              planName: '',
                              planSpeed: '',
                              planPrice: ''
                            });
                          }}
                        >
                          <option value="">-- Choose Service --</option>
                          {services.map(service => (
                            <option key={service.name} value={service.name}>{service.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="font-semibold">Select Plan *</Label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={newOrderData.planName}
                          onChange={(e) => {
                            const availablePlans = getAvailablePlans();
                            const selectedPlan = availablePlans.find(p => p.name === e.target.value);
                            if (selectedPlan) {
                              setNewOrderData({
                                ...newOrderData,
                                planName: selectedPlan.name,
                                planSpeed: selectedPlan.speed
                              });
                            } else {
                              setNewOrderData({...newOrderData, planName: '', planSpeed: ''});
                            }
                          }}
                          disabled={!newOrderData.serviceType}
                        >
                          <option value="">
                            {newOrderData.serviceType ? 'Select Plan' : 'First select service'}
                          </option>
                          {getAvailablePlans().map(plan => (
                            <option key={plan.name} value={plan.name}>
                              {plan.name} - {plan.speed}
                            </option>
                          ))}
                        </select>
                        {!newOrderData.serviceType && (
                          <p className="text-xs text-red-600 mt-1">⚠ Select service first</p>
                        )}
                      </div>
                      <div>
                        <Label>Installation Date</Label>
                        <Input
                          type="date"
                          value={newOrderData.installationDate}
                          onChange={(e) => setNewOrderData({...newOrderData, installationDate: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Plan Price (₹) *</Label>
                        <Input
                          type="number"
                          value={newOrderData.planPrice}
                          onChange={(e) => setNewOrderData({...newOrderData, planPrice: e.target.value})}
                          placeholder="Enter monthly price"
                          min="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">Admin decides the pricing</p>
                      </div>
                      <div>
                        <Label>OTC Amount (₹) *</Label>
                        <Input
                          type="number"
                          value={newOrderData.otcAmount}
                          onChange={(e) => setNewOrderData({...newOrderData, otcAmount: e.target.value})}
                          placeholder="One-time charges"
                          min="0"
                        />
                      </div>
                      {(newOrderData.planPrice || newOrderData.otcAmount) && (
                        <div className="md:col-span-2 bg-white p-3 rounded border-2 border-purple-300">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700 font-semibold">Total Amount:</span>
                            <span className="text-2xl font-bold text-purple-700">
                              ₹{(parseFloat(newOrderData.planPrice || 0) + parseFloat(newOrderData.otcAmount || 0)).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Documents Upload Section */}
                  <div className="bg-orange-50 p-2 rounded-lg border border-orange-200">
                    <h4 className="text-sm font-semibold text-orange-900 mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Document Uploads
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <Label>ID Proof (Aadhar/PAN/Passport)</Label>
                        <Input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setIdProofFile(e.target.files[0])}
                          className="cursor-pointer"
                        />
                        {idProofFile && (
                          <p className="text-xs text-green-600 mt-1">✓ {idProofFile.name}</p>
                        )}
                      </div>
                      <div>
                        <Label>Address Proof (Electricity Bill/Rent Agreement)</Label>
                        <Input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setAddressProofFile(e.target.files[0])}
                          className="cursor-pointer"
                        />
                        {addressProofFile && (
                          <p className="text-xs text-green-600 mt-1">✓ {addressProofFile.name}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Details Section */}
                  <div className="bg-gray-50 p-2 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Additional Details
                    </h4>
                    <div className="space-y-2">
                      <div>
                        <Label>Priority Level</Label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={newOrderData.priority}
                          onChange={(e) => setNewOrderData({...newOrderData, priority: e.target.value})}
                        >
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                      <div>
                        <Label>Notes / Special Instructions</Label>
                        <textarea
                          className="w-full min-h-[60px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={newOrderData.notes}
                          onChange={(e) => setNewOrderData({...newOrderData, notes: e.target.value})}
                          placeholder="Any special requirements or notes..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-4 pt-3 border-t">
                  <Button
                    onClick={handleCreateNewOrder}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    disabled={creatingOrder}
                  >
                    {creatingOrder ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Order...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Create Order
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowNewOrderModal(false)}
                    variant="outline"
                    className="flex-1"
                    disabled={creatingOrder}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderManagement;
